/**
 * useStreakHeartbeat — fires `POST /api/streak/touch` once per local day
 * for the signed-in user, but ONLY when they're visiting a reader- or
 * writer-related part of the app (Jun 5 2026 user spec).
 *
 * The login route itself already bumps the streak server-side, so this
 * hook covers users who land directly on a deep-linked page after a
 * refresh (no re-login) but should NEVER fire on neutral pages like
 * /auth/callback, /admin (purely admin work isn't reader/writer
 * activity), or any unauthenticated route.
 *
 * Path classification:
 *   • reader: /, /dashboard, /library, /reader/*, /book/*, /discover,
 *             /shop, /progress, /profile, /book-club, /vault, /quests
 *   • writer: /manage-content, /writer-journey, /uploads, /writer-stats,
 *             /writer-spotlight, /writer-dashboard
 *   • neither (skip): /admin, /admin-panel, /admin-login, /writer-login,
 *                     /user-login, /auth/*
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const LS_KEY = 'sy_streak_touch_last_local_day';

const READER_PREFIXES = [
  '/dashboard', '/library', '/reader', '/book', '/discover', '/shop',
  '/progress', '/profile', '/book-club', '/vault', '/quests', '/audiobook',
  '/series', '/cinematic',
];
const WRITER_PREFIXES = [
  '/manage-content', '/writer-journey', '/uploads', '/writer-stats',
  '/writer-spotlight', '/writer-dashboard', '/upload',
];
const SKIP_PREFIXES = [
  '/admin', '/auth', '/login', '/user-login', '/writer-login',
  '/admin-login',
];

const isStreakablePath = (pathname) => {
  const p = (pathname || '').toLowerCase();
  if (SKIP_PREFIXES.some((s) => p === s || p.startsWith(`${s}/`))) return false;
  if (p === '/') return true; // root resolves to dashboard for logged-in readers
  if (READER_PREFIXES.some((s) => p === s || p.startsWith(`${s}/`))) return true;
  if (WRITER_PREFIXES.some((s) => p === s || p.startsWith(`${s}/`))) return true;
  return false;
};

const todayLocalISO = () => {
  const d = new Date();
  const offsetMs = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 10);
};

export default function useStreakHeartbeat(user) {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!user || !user.user_id) return;
    if (!isStreakablePath(pathname)) return;

    const today = todayLocalISO();
    const stored = localStorage.getItem(LS_KEY);
    const lastDayForThisUser = stored && stored.startsWith(`${user.user_id}:`)
      ? stored.split(':')[1]
      : null;
    if (lastDayForThisUser === today) return;

    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/streak/touch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-TZ-Offset-Min': String(new Date().getTimezoneOffset()),
          },
        });
        if (r.ok) {
          localStorage.setItem(LS_KEY, `${user.user_id}:${today}`);
        }
      } catch {
        // Non-fatal — next navigation will retry.
      }
    })();
  }, [user, pathname]);
}
